import Blob from "cross-blob";
import ExcelJS from "exceljs";

import { ApplicantProjectFull } from "@/lib/prisma";

import { getApplicantRefereesWithScoring } from "@/core/client/application/applicantReferee";

import { DocGenerator } from "../";
import { parseProseMirrorSchema } from "../../proseMirror";

interface RefMapXlsxArgs {
  applicantProjectList: ApplicantProjectFull[];
}

export const createRefMapXlsx: DocGenerator<RefMapXlsxArgs> = async (
  t,
  { application, applicantProjectList }
) => {
  const refereesWithScoring = await getApplicantRefereesWithScoring(
    application.id
  );

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("My Sheet");

  worksheet.columns = [
    { key: "name", width: 20 },
    { key: "rel", width: 20 },
    { key: "sig", width: 32 },
    { key: "prj", width: 32 },
    { key: "link", width: 20 },
    { key: "workAt", width: 32 },
    { key: "workNowAt", width: 32 },
    { key: "howLong", width: 32 },
    { key: "tags", width: 60 },
    { key: "score", width: 15 },
    { key: "chosen", width: 15 },
    { key: "comments", width: 20 },
  ];

  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };

  headerRow.values = {
    name: "Name Surname",
    rel: "Relationship.",
    sig: "Signature likelihood.",
    prj: "Joint Project(s)",
    link: "Linkedin",
    workAt: "Worked together at",
    workNowAt: "Working now at",
    howLong: "How long ....",
    canSign: "Referee can sign",
    tags: "Tags",
    score: "Score",
    chosen: "Chosen?",
    comments: "Comments",
  };
  headerRow.commit();

  refereesWithScoring.forEach((item, i) => {
    const row = worksheet.getRow(i + 2); // numbering starts from 1 and one row under header
    const prjs = applicantProjectList.filter((prj) =>
      item.experienceListAssigned.includes(prj.id)
    );
    const companies = new Set(
      prjs.map((prj) => parseProseMirrorSchema(prj.applicantCompany.name))
    );

    row.values = {
      name: `${item.firstName} ${item.lastName}`,
      rel: item.relationshipTag?.name ?? "-",
      sig: item.signatureLikelihoodTag?.name ?? "-",
      prj: prjs.map((prj) => parseProseMirrorSchema(prj.name)).join(", "),
      link: item.linkedInUrl,
      workAt: item.workedTogether ? Array.from(companies).join(", ") : "",
      workNowAt: item.workingNowInCompanyWorkedTogether
        ? Array.from(companies).join(", ")
        : "",
      howLong:
        item.howLongKnowYou === "more_12_month"
          ? "More than 12 months"
          : "Less than 12 months",
      workedTogether: item.workedTogether ? "Yes" : "No",
      canSign: item.signCapability
        ?.split(",")
        .map(
          (slug: string) =>
            ({
              "sign-screenshot": "Sign with screenshot",
              "sign-kpi": "Sign with kpi",
            }[slug])
        )
        .join(", "),
      tags: item.features.map((ft) => ft.name).join(", "),
      score: item.scoringResult,
      chosen: item.chosen ? "Yes" : "No",
      comments: "",
    };

    const tagsCell = row.getCell(9);
    tagsCell.style = { alignment: { wrapText: true } };

    row.commit();
  });

  return new Blob([await workbook.xlsx.writeBuffer()]);
};
